/**
 * Один байк из двух источников.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ЗАЧЕМ ЭТО СУЩЕСТВУЕТ
 * ─────────────────────────────────────────────────────────────────────────
 * До объединения карточек было два экрана про один и тот же байк. Задача
 * (`/v1/scout/tasks/*`) знает IMEI и умеет командовать замком; парк
 * (`/v1/scout/vehicles`) знает свежий статус и умеет менять его. Заряд,
 * замок и координаты знали оба — и расходились.
 *
 * Экран теперь один, а источников по-прежнему два, и байк может быть
 * известен из любого из них:
 *
 *   только парк   — обычный случай: открыли с карты байк, задачи нет;
 *   только задача — байк вне скаутской проекции (уже увезли, списали,
 *                   отфильтровали) — но задача на него у скаута на руках;
 *   оба           — приехали по задаче, байк ещё и в парке.
 *
 * ⚠ ТРЕТИЙ СЛУЧАЙ — НЕ «ОДИН ИЗ ДВУХ», А ИХ СМЕСЬ. Свежесть у полей разная:
 * список парка перезапрашивается каждые 30 секунд, а поля в задаче — снимок
 * на момент её создания (`fleet/internal/biz/maintenance.go`, attachVehicles).
 * Поэтому телеметрия и статус берутся из парка, когда он есть, а IMEI — из
 * задачи всегда: парк его не отдаёт вовсе (см. `api/vehicles.ts`).
 */
import type { ScoutVehicle } from '@/api/vehicles'
import type { ScoutTask } from '@/api/scout'
import type { ScoutDevice } from '@/api/device'

export type BikeView = {
  vehicleId: string
  /** Номер с наклейки — «A17». Пустой, если наклейка не назначена. */
  qrToken: string
  /** C1: человеческий номер. До выката совпадает с qrToken — см. displayNumber. */
  number: string
  vehicleType: string
  /** Статус транспорта. Пустой — источник его не знает. */
  status: string
  batteryPct: number
  locked: boolean
  /** Три состояния, как на проводе: undefined / true / false. */
  runtimeStale: boolean | undefined
  lat: number
  lng: number
  lastSeenAt: string
  /** C1 — см. api/vehicles.ts про то, почему пустая строка не значит «нет проблем». */
  problem: string
  problemDetail: string
  faultReason: string
  lockedConfirmedAt: string
  /** Пустой, когда байк известен только из парка: проекция IMEI не отдаёт. */
  imei: string
  /** Задача на этот байк, если она у скаута на руках или лежит в пуле. */
  task: ScoutTask | null
  /** Задача взята текущим скаутом — от этого зависят кнопки замка. */
  claimed: boolean
  /**
   * Тот же байк в форме ScoutVehicle.
   *
   * Нужен, чтобы не переписывать под BikeView функции, которые уже работают
   * и уже проверены: describeBike, rideZoneVerdict.
   */
  vehicle: ScoutVehicle
}

/** Номер с наклейки для показа. C1-поле или, до его выката, qrToken. */
export function displayNumber(v: Pick<BikeView, 'number' | 'qrToken'>): string {
  return v.number || v.qrToken
}

/**
 * Собрать вид байка.
 *
 * `null` — обоих источников нет: байка нет ни в парке, ни в задачах. Это
 * ответ, а не ошибка: список парка мог не доехать, задачу мог закрыть
 * диспетчер. Решает, что показать, экран.
 */
export function mergeBikeView(
  vehicle: ScoutVehicle | undefined,
  task: ScoutTask | undefined,
): BikeView | null {
  if (!vehicle && !task) return null

  const vehicleId = vehicle?.id ?? task?.vehicleId ?? ''
  const qrToken = vehicle?.qrToken || task?.qrToken || ''

  /*
   * Телеметрия берётся ЦЕЛИКОМ из одного источника, а не по полю.
   *
   * Смешать «заряд из парка» с «замком из задачи» значит показать состояние,
   * которого не было ни в один момент времени: снимки сняты в разные минуты,
   * и `runtimeStale` относится к своему снимку, а не к обоим.
   */
  const live = vehicle ?? task
  const known = vehicle ?? task

  // C1-поля живут только на ScoutVehicle — у задачи их нет вовсе, поэтому
  // «только задача» видит их пустыми, а не выдуманными.
  const merged: ScoutVehicle = {
    id: vehicleId,
    qrToken,
    number: vehicle?.number ?? '',
    vehicleType: vehicle?.vehicleType ?? '',
    // У задачи статус транспорта лежит под другим именем — vehicleStatus.
    status: vehicle?.status || task?.vehicleStatus || '',
    batteryPct: live?.batteryPct ?? 0,
    locked: live?.locked ?? false,
    lat: known?.lat ?? 0,
    lng: known?.lng ?? 0,
    lastSeenAt: known?.lastSeenAt ?? '',
    problem: vehicle?.problem ?? '',
    problemDetail: vehicle?.problemDetail ?? '',
    faultReason: vehicle?.faultReason ?? '',
    lockedConfirmedAt: vehicle?.lockedConfirmedAt ?? '',
    labels: vehicle?.labels ?? [],
    poolDriverId: vehicle?.poolDriverId ?? '',
    poolSince: vehicle?.poolSince ?? '',
    poolDeliveredAt: vehicle?.poolDeliveredAt ?? '',
    poolKind: vehicle?.poolKind ?? '',
    ...(live?.runtimeStale === undefined ? {} : { runtimeStale: live.runtimeStale }),
  }

  // Поля перечислены явно, а не разложены из `merged`: у BikeView ключ
  // называется `vehicleId`, у ScoutVehicle — `id`, и spread молча оставил бы
  // `vehicleId` пустым, не подав ни одного признака на типах.
  return {
    vehicleId,
    qrToken,
    number: merged.number,
    vehicleType: merged.vehicleType,
    status: merged.status,
    batteryPct: merged.batteryPct,
    locked: merged.locked,
    runtimeStale: live?.runtimeStale,
    lat: merged.lat,
    lng: merged.lng,
    lastSeenAt: merged.lastSeenAt,
    problem: merged.problem,
    problemDetail: merged.problemDetail,
    faultReason: merged.faultReason,
    lockedConfirmedAt: merged.lockedConfirmedAt,
    imei: task?.imei ?? '',
    task: task ?? null,
    claimed: task?.status === 'claimed',
    vehicle: merged,
  }
}

/**
 * Наложить живой опрос устройства («Обновить GPS», §2.2.1) поверх снимка из
 * парка/задачи.
 *
 * ⚠ КООРДИНАТЫ — ТОЛЬКО ПРИ `locationValid`. Замок без спутников отвечает
 * нулями, а (0, 0) — это Гвинейский залив, а не «не знаю» (та же причина,
 * по которой `api/device.ts` держит `locationValid` отдельно от lat/lng).
 * Заряд и замок применяются всегда: сам факт ответа устройства уже
 * подтверждает, что это не выдуманные нули.
 *
 * ⚠ `runtimeStale` И `lastSeenAt` ОБЯЗАНЫ ДВИГАТЬСЯ ВМЕСТЕ. `bike-header.tsx`
 * показывает заряд только при `runtimeStale === false` (протухший снимок —
 * заведомая ложь про заряд), а «связь» читает `runtimeStale` И `lastSeenAt`
 * разом. Обновить только заряд/замок, оставив `runtimeStale` из старого
 * снимка паркинга, — ровно тот байк, ради которого кнопку и нажимают
 * (протухшая телеметрия), продолжил бы показывать «—» вместо только что
 * пришедшего заряда. Успешный ответ устройства — сам по себе доказательство
 * связи прямо сейчас, поэтому оба поля переходят в «свежо, только что».
 *
 * `device` не пришёл (кнопку ещё не нажимали, либо опрос отказал) — исходный
 * вид возвращается как есть, без изменений; `readAt` тогда не используется.
 */
export function applyDeviceReading(
  view: BikeView,
  device: ScoutDevice | undefined,
  readAt: string,
): BikeView {
  if (!device) return view
  const position = device.locationValid ? { lat: device.lat, lng: device.lng } : {}
  return {
    ...view,
    batteryPct: device.batteryPct,
    locked: device.locked,
    runtimeStale: false,
    lastSeenAt: readAt,
    ...position,
    vehicle: {
      ...view.vehicle,
      batteryPct: device.batteryPct,
      locked: device.locked,
      runtimeStale: false,
      lastSeenAt: readAt,
      ...position,
    },
  }
}

/**
 * Найти задачу по этому байку.
 *
 * Взятая перевешивает лежащую в пуле: кнопки замка гейтятся именно взятой,
 * и показать вместо неё чужую открытую задачу значит нарисовать активные
 * кнопки, которые вернут 403.
 */
export function taskForVehicle(
  vehicleId: string,
  tasks: readonly ScoutTask[],
): ScoutTask | undefined {
  if (vehicleId === '') return undefined
  const mine = tasks.find((t) => t.vehicleId === vehicleId && t.status === 'claimed')
  return mine ?? tasks.find((t) => t.vehicleId === vehicleId)
}
